<template>
  <div class="channels-page">
    <header class="channels-header">
      <div class="channels-header__titles">
        <h1 class="channels-title">网页渠道集成</h1>
        <p class="channels-subtitle">配置您的网站聊天窗并获取集成代码</p>
      </div>
      <div class="channels-header__actions">
        <el-button plain icon="el-icon-view" @click="onPreview">实时预览</el-button>
        <el-button type="primary" @click="onPublish">发布更改</el-button>
      </div>
    </header>

    <el-row :gutter="20" class="channels-row">
      <el-col :span="18" :xs="24">
        <el-card class="channels-card channels-card--chat-link" shadow="never">
          <div slot="header" class="card-header-row">
            <span class="card-header-title">
              <img src="@/assets/image/copy-icon.png" class="card-header-icon" alt="" />
              聊天链接
            </span>
          </div>
          <p class="card-desc">您可以直接将此链接发送给客户，或将其嵌入到按钮点击事件中。</p>
          <div class="chat-link-strip">
            <span class="chat-link-strip__text">{{ chatLinkUrl }}</span>
            <div class="chat-link-strip__actions">
              <el-tooltip content="打开链接" placement="top">
                <img
                  src="@/assets/image/open-icon.png"
                  class="chat-link-strip__external"
                  alt=""
                  role="button"
                  tabindex="0"
                  @click="openChatLink"
                  @keyup.enter.prevent="openChatLink"
                  @keyup.space.prevent="openChatLink"
                />
              </el-tooltip>
              <el-button class="chat-link-strip__copy" icon="el-icon-document-copy" @click="copyChatLink">复制链接</el-button>
            </div>
          </div>
        </el-card>

        <el-card class="channels-card channels-card--header-plain" shadow="never">
          <div slot="header" class="card-header-row">
            <span class="card-header-title">
              <img src="@/assets/image/daima-icon.png" class="icon-box" alt="" />
              邀请链接说明
            </span>
            <span class="card-tag-rec">推荐方式</span>
          </div>
          <p class="card-desc">将下方说明复制给对接方，用于拼接访客聊天入口链接。</p>
          <div class="code-panel">
            <div class="code-panel__toolbar">
              <button type="button" class="code-panel__fs-btn" aria-label="全屏查看全文" @click="snippetFullscreenVisible = true">
                <span aria-hidden="true">⛶</span>
              </button>
              <el-button type="primary" size="small" icon="el-icon-document-copy" @click="copyWidgetCode">复制说明</el-button>
            </div>
            <pre class="code-panel__pre"><code class="doc-snippet doc-snippet--invite" v-html="widgetSnippetHtml"></code></pre>
          </div>
        </el-card>

        <el-card class="channels-card channels-card--header-plain" shadow="never">
          <div slot="header" class="card-header-row">
            <span class="card-header-title">
              <img src="@/assets/image/fk-iocn.png" class="icon-box" alt="" />
              访客端设置
            </span>
          </div>
          <div class="visitor-setting">
            <div class="visitor-setting__label">
              <span class="visitor-setting__name">语言</span>
              <span class="visitor-setting__hint">访客端将按当前选择的语言显示</span>
            </div>
            <el-select v-model="visitorLang" placeholder="请选择语言" class="visitor-setting__select">
              <el-option label="中文" value="zh-CN" />
              <el-option label="English" value="en-US" />
            </el-select>
          </div>
        </el-card>
      </el-col>

      <el-col :span="6" :xs="24">
        <el-card class="channels-card channels-card--aside channels-card--guide-flush" shadow="never">
          <div class="guide-thumb">
            <img src="@/assets/image/minb.png" class="guide-thumb__img" alt="" />
          </div>
          <h3 class="aside-title">快速指南</h3>
          <p class="aside-desc">如果您在集成过程中遇到问题，请查阅我们的集成手册或联系技术支持。</p>
          <div class="aside-links">
            <a href="#" class="aside-link" @click.prevent="onDocClick">
              <img src="@/assets/image/book.png" class="aside-link__icon" alt="" />
              查看 JavaScript API 文档
            </a>
            <a href="#" class="aside-link" @click.prevent="onSupportClick">
              <img src="@/assets/image/zhichi.png" class="aside-link__icon" alt="" />
              联系集成工程师
            </a>
          </div>
        </el-card>

        <el-card class="channels-card" shadow="never">
          <div slot="header" class="card-header-row">
            <span class="card-header-title">
              <img src="@/assets/image/dun.png" class="card-header-icon card-header-icon--shield" alt="" />
              网址安全检测
            </span>
            <el-button type="primary" @click="getDetect" :loading="btnLoading">检测</el-button>
          </div>
          <el-scrollbar style="height: 50vh" v-loading="btnLoading">
            <div>
              <div class="security-grid" v-if="securityChecks && securityChecks.length > 0">
                <div v-for="(item, index) in securityChecks" :key="index" class="security-cell">
                  <div class="security-cell__logo-wrap">
                    <img class="security-cell__img" :src="item.detectIconUrl" :alt="item.shortName" />
                  </div>
                  <div class="security-cell__body">
                    <span class="security-cell__name">{{ item.shortName }}</span>
                    <div class="security-cell__status" :class="'is-' + item.status">
                      <span class="security-dot" />
                      {{ item.statusText }}
                    </div>
                  </div>
                </div>
              </div>
              <template v-else>
                <el-empty description="暂无安全检测数据" :image-size="80"></el-empty>
              </template>
            </div>
          </el-scrollbar>
        </el-card>
      </el-col>
    </el-row>

    <el-dialog
      title="邀请链接说明（全文）"
      :visible.sync="snippetFullscreenVisible"
      width="800px"
      append-to-body
      custom-class="channels-snippet-dialog"
    >
      <div class="channels-snippet-dialog__scroll">
        <pre class="channels-snippet-pre"><code class="doc-snippet doc-snippet--invite" v-html="widgetSnippetHtml"></code></pre>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="snippetFullscreenVisible = false">关 闭</el-button>
        <el-button type="primary" icon="el-icon-document-copy" @click="copyWidgetCode">复制说明</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import qq from "@/assets/image/qqq.png";
import wx from "@/assets/image/wx.png";
import hw from "@/assets/image/hw.png";
import wr from "@/assets/image/wr.png";
import bd from "@/assets/image/bd.png";
import gg from "@/assets/image/gg.png";
import vv from "@/assets/image/vv.png";
import xm from "@/assets/image/xm.png";
import op from "@/assets/image/op.png";

export default {
  name: "channels",

  data() {
    return {
      visitorLang: "zh-CN",
      snippetFullscreenVisible: false,
      widgetConfig: {
        domain: "jinketong.example.com"
      },
      securityChecks: [
        // { key: "qq", img: qq, name: "QQ 检测", status: "ok", statusText: "正常访问" },
        // { key: "wechat", img: wx, name: "微信", status: "ok", statusText: "正常访问" },
        // { key: "huawei", img: hw, name: "华为", status: "ok", statusText: "正常访问" },
        // { key: "microsoft", img: wr, name: "Microsoft", status: "ok", statusText: "正常访问" },
        // { key: "baidu", img: bd, name: "百度", status: "ok", statusText: "正常访问" },
        // { key: "google", img: gg, name: "Google", status: "ok", statusText: "正常访问" },
        // { key: "apple", img: gg, name: "Apple", status: "ok", statusText: "正常访问" },
        // { key: "vivo", img: vv, name: "vivo", status: "warn", statusText: "提示跳转" },
        // { key: "xiaomi", img: xm, name: "小米", status: "ok", statusText: "正常访问" },
        // { key: "oppo", img: op, name: "OPPO", status: "danger", statusText: "异常拦截" }
      ],
      btnLoading: false
    };
  },

  computed: {
    chatLinkUrl() {
      return this.userStore.userInfo.serviceUrl;
    },
    /** 与 public/doctom.html 邀请链接文档一致，便于客服后台一键复制 */
    widgetSnippet() {
      const { domain } = this.widgetConfig;
      const host = domain || "域名";
      return `<!--
  邀请链接文档

  链接结构说明：若只填域名、不带 ID 与签名，则随机分配在线客服。

  【完整链接格式】
  https://${host}/#/visitors/chat?customerId=ID&encrypted=签名&id={id}&username={username}&nickname={nickname}

  【固定部分】（一般不需改）
  https://${host}/#/visitors/chat?customerId=ID&encrypted=签名

  【可变参数】
  id — 用户ID
  username — 用户名
  nickname — 昵称

  【示例 1：完整参数】
  https://${host}/#/visitors/chat?customerId=ID&encrypted=签名&id=123&username=john&nickname=JohnDoe

  【示例 2：部分参数】
  https://${host}/#/visitors/chat?customerId=ID&encrypted=签名&id=456&username=alice

  【示例 3：仅固定部分】
  https://${host}/#/visitors/chat?customerId=ID&encrypted=签名&id=&username=&nickname=

  【注意事项】
  - 将域名替换为后台配置的服务器域名，也可用 http://服务器IP:端口。
  - 参数值需 URL 编码；不需要的参数可传空值。
  - customerId、encrypted 固定部分必须保持不变。
-->`;
    },

    /** 展示用：纯文本已 escape，仅允许受控的 span 高亮（复制仍用 widgetSnippet） */
    widgetSnippetHtml() {
      return this.widgetSnippet
        .split("\n")
        .map((line) => this.highlightInviteDocLine(line))
        .join("\n");
    }
  },

  methods: {
    escapeHtml(str) {
      return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    },

    /** 单行高亮规则：按需在正则里增减关键字即可 */
    highlightInviteDocLine(line) {
      let s = this.escapeHtml(line);
      s = s.replace(/(【[^】]+】)/g, '<span class="doc-snippet__hl doc-snippet__hl--title">$1</span>');
      s = s.replace(/(https?:\/\/[^\s]+)/g, '<span class="doc-snippet__hl doc-snippet__hl--url">$1</span>');
      s = s.replace(
        /^(\s*)(id|username|nickname)(\s+—)/,
        (_, sp, name, rest) => `${sp}<span class="doc-snippet__hl doc-snippet__hl--param">${name}</span>${rest}`
      );
      s = s.replace(/\b(customerId|encrypted)\b/g, '<span class="doc-snippet__hl doc-snippet__hl--param">$1</span>');
      return s;
    },

    copyText(text, okMsg) {
      const done = () => this.$message.success(okMsg || "已复制到剪贴板");
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(text)
          .then(done)
          .catch(() => this.fallbackCopy(text, okMsg));
      } else {
        this.fallbackCopy(text, okMsg);
      }
    },

    fallbackCopy(text, okMsg) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;opacity:0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        this.$message.success(okMsg || "已复制到剪贴板");
      } catch (e) {
        this.$message.error("复制失败，请手动复制");
      }
      document.body.removeChild(ta);
    },

    copyChatLink() {
      this.copyText(this.chatLinkUrl, "链接已复制");
    },

    copyWidgetCode() {
      this.copyText(this.widgetSnippet, "说明已复制");
    },

    openChatLink() {
      window.open(this.chatLinkUrl, "_blank", "noopener,noreferrer");
    },

    onPreview() {
      this.$message.info("暂未开放！");
    },

    onPublish() {
      this.$message.info("暂未开放！");
    },

    onDocClick() {
      const base = process.env.BASE_URL || "/";
      const path = `${base.replace(/\/?$/, "/")}doctom.html`;
      window.open(path, "_blank", "noopener,noreferrer");
    },

    onSupportClick() {
      this.$message.info("暂未开放！");
    },
    // 获取所有域名检测
    getDetect() {
      this.btnLoading = true;
      this.$http({
        url: "/customer/detect",
        method: "post"
      })
        .then((res) => {
          console.log("获取域名检测", res);
          this.securityChecks = res.map((item) => {
            return {
              ...item,
              status: item.statu ? "ok" : "danger",
              statusText: item.statu ? "正常访问" : "异常拦截"
            };
          });
        })
        .finally(() => {
          this.btnLoading = false;
        });
    }
  }
};
</script>

<style scoped lang="scss">
.channels-page {
  box-sizing: border-box;
  width: 100%;
  height: calc(100vh - 65px);
  overflow-y: auto;
  padding: 20px 24px 32px;
  background-color: #f5f6f7;
}

.channels-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.channels-title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
}

.channels-subtitle {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.channels-header__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.channels-row {
  align-items: flex-start;
}

.channels-card {
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #c3c6d7;

  &--aside {
    background: #ededf8;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &--chat-link ::v-deep .el-card__header {
    border-bottom: none;
  }

  &--header-plain ::v-deep .el-card__header {
    padding: 14px 18px;
    border-bottom: none;
  }

  ::v-deep .el-card__header {
    padding: 14px 18px;
  }

  ::v-deep .el-card__body {
    padding: 18px;
  }

  /* 快速指南：配图与卡片内沿顶/左/右贴齐（需写在通用 body padding 之后，否则会被覆盖） */
  &--guide-flush {
    overflow: hidden;

    ::v-deep .el-card__body {
      padding: 0;
    }
  }
}

.card-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
}

.card-tag-rec {
  flex-shrink: 0;
  background: #d2ffd5;
  color: #00714a;
  font-size: 13px;
  padding: 5px 10px;
  border-radius: 4px;
}

.card-header-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.card-header-icon {
  width: 16px;
  height: auto;
  vertical-align: middle;

  &--shield {
    width: 16px;
    height: 20px;
    object-fit: contain;
  }
}

.icon-box {
  width: 16px;
  height: auto;
  vertical-align: middle;
}

.card-desc {
  margin: 0 0 14px;
  font-size: 14px;
  color: #737686;
  line-height: 1.6;
  text-align: left;

  &__code {
    padding: 0 4px;
    font-size: 12px;
    font-family: Consolas, Menlo, monospace;
    background: rgba(0, 0, 0, 0.06);
    border-radius: 3px;
    color: #374151;
  }
}

.chat-link-strip {
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  background: #f3f3fe;
  border-radius: 4px;
  overflow: hidden;
  text-align: left;
  border: 1px solid #c3c6d7;
}

.chat-link-strip__text {
  flex: 1;
  min-width: 160px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
  color: #303133;
  word-break: break-all;
  display: flex;
  align-items: center;
}

.chat-link-strip__actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px 10px 6px 8px;
}

.chat-link-strip__external {
  box-sizing: content-box;
  width: 15px;
  height: 15px;
  padding: 6px;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    border-radius: 4px;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.35);
  }
}

.chat-link-strip__copy {
  margin: 0 !important;
  padding: 8px 14px !important;
  height: auto !important;
  line-height: 1.4 !important;
  background: #fff !important;
  border: 1px solid #dcdfe6 !important;
  color: #606266 !important;
  border-radius: 4px !important;
  font-weight: 400;

  &:hover,
  &:focus {
    background: #f5f7fa !important;
    border-color: #c0c4cc !important;
    color: #303133 !important;
  }
}

.code-panel {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #2d2d2d;
  background: #1e1e1e;
}

.code-panel__toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 12px;
}

.code-panel__fs-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  border: 1px dashed #c0c4cc;
  background: transparent;
  color: #909399;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  user-select: none;

  &:hover,
  &:focus {
    border-color: #409eff;
    color: #409eff;
    outline: none;
  }
}

.code-panel__pre {
  margin: 0;
  padding: 16px 18px;
  max-height: 260px;
  overflow-x: auto;
  overflow-y: auto;
  font-family: "Consolas", "Menlo", "Monaco", monospace;
  font-size: 12px;
  line-height: 1.65;
  color: #d4d4d4;
  background: #1e1e1e;
  text-align: left;

  code {
    white-space: pre;
    font-family: inherit;
  }
}

.visitor-setting {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 320px;
}

.visitor-setting__label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.visitor-setting__name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.visitor-setting__hint {
  font-size: 12px;
  color: #909399;
}

.visitor-setting__select {
  width: 100%;
}

.guide-thumb {
  margin: 0;
  padding: 0;
  overflow: hidden;
  line-height: 0;
}

.guide-thumb__img {
  display: block;
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.aside-title {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 500;
  color: #003da6;
  text-align: left;
  padding: 14px 10px 0;
}

.aside-desc {
  margin: 0 0 14px;
  font-size: 13px;
  color: #434654;
  line-height: 1.55;
  text-align: left;
  padding: 0 10px;
}

.aside-links {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 10px 18px;
}

.aside-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #2563eb;
  text-decoration: none;

  &__icon {
    width: 14px;
    height: auto;
    flex-shrink: 0;
  }

  &:hover {
    text-decoration: underline;
  }
}

.security-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.security-cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #c2c6d8;
  background: #f1f3ff;
  cursor: pointer;
  &:hover {
    //
    filter: blur(1px);
  }
}

.security-cell__logo-wrap {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid rgba(194, 198, 216, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.security-cell__img {
  width: 26px;
  height: 26px;
  object-fit: contain;
  display: block;
}

.security-cell__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
}

.security-cell__name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
}

.security-cell__status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  line-height: 1.3;
  color: #6b7280;

  &.is-ok {
    color: #006e1c;

    .security-dot {
      background: #006e1c;
    }
  }

  &.is-warn {
    color: #c67a00;

    .security-dot {
      background: #c67a00;
    }
  }

  &.is-danger {
    color: #cf2504;

    .security-dot {
      background: #cf2504;
    }
  }
}

.security-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #cbd5e1;
}

@media (max-width: 992px) {
  .channels-row .el-col {
    margin-bottom: 8px;
  }

  .channels-page {
    padding: 16px 12px 28px;
  }

  .chat-link-strip {
    flex-direction: column;
  }

  .chat-link-strip__actions {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .security-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
/* append-to-body 对话框挂在 body，需非 scoped 样式 */
.channels-snippet-dialog {
  .el-dialog__body {
    padding: 12px 20px 8px;
  }

  &__scroll {
    max-height: calc(90vh - 220px);
    overflow: auto;
    border-radius: 8px;
    border: 1px solid #2d2d2d;
    background: #1e1e1e;
  }

  .channels-snippet-pre {
    margin: 0;
    padding: 16px 18px;
    overflow-x: auto;
    font-family: Consolas, Menlo, Monaco, monospace;
    font-size: 12px;
    line-height: 1.65;
    color: #d4d4d4;
    background: transparent;
    text-align: left;

    code {
      white-space: pre;
      font-family: inherit;
    }
  }
}

/* v-html 内 span 无 scoped data 属性，单独挂在 doc-snippet 命名空间下 */
.doc-snippet.doc-snippet--invite .doc-snippet__hl--title {
  color: #7ee0a1;
  font-weight: 600;
}

.doc-snippet.doc-snippet--invite .doc-snippet__hl--url {
  color: #5898e6;
}

.doc-snippet.doc-snippet--invite .doc-snippet__hl--param {
  color: #f472b6;
}
</style>
